// Shared ATW path boundary. Decode URL spelling ONCE at the client edge; handlers consume the
// resulting decoded path without another decode. In particular %252e is a literal %2e filename.
declare const atwPathBrand: unique symbol;
export type AtwPath = string & { readonly [atwPathBrand]: true };
export const MAX_ATW_PATH_LENGTH = 2048;

export class AtwPathError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "AtwPathError";
  }
}

function normalizeDecoded(path: string): string {
  if (path.length > MAX_ATW_PATH_LENGTH) throw new AtwPathError("path exceeds the 2048-character handler limit");
  const normalized = path.normalize("NFC");
  if (/[\p{Cc}\\]/u.test(normalized)) throw new AtwPathError("path contains a control character or backslash");
  try { encodeURIComponent(normalized); }
  catch { throw new AtwPathError("path contains invalid Unicode"); }
  const segments: string[] = [];
  const parts = normalized.split("/");
  for (const segment of parts) {
    if (segment === "" || segment === ".") continue;
    if (segment === "..") {
      if (segments.length === 0) throw new AtwPathError("path ascends above the site root");
      segments.pop();
    } else segments.push(segment);
  }
  const trailing = normalized.endsWith("/") || parts.at(-1) === "." || parts.at(-1) === "..";
  return `/${segments.join("/")}${trailing && segments.length > 0 ? "/" : ""}`;
}

/** Raw encoded PATH only, not a whole URL, query or fragment. Call before a URL parser can clamp
 * traversal away. The returned brand is shared by escape checks and handler lookup. */
export function canonicalizeAtwPath(encodedPath: string): AtwPath {
  if (typeof encodedPath !== "string" || encodedPath.length > MAX_ATW_PATH_LENGTH || /[?#]/.test(encodedPath)) {
    throw new AtwPathError("expected a path of at most 2048 characters, with query/fragment separate");
  }
  let decoded: string;
  try { decoded = decodeURIComponent(encodedPath); }
  catch { throw new AtwPathError("path contains malformed percent-encoding or UTF-8"); }
  return normalizeDecoded(decoded) as AtwPath;
}

/** Validate an ALREADY DECODED canonical path (signed manifest key or client-normalized input).
 * No percent decoding here: normalizing an encoded spelling twice creates traversal bugs. */
export function assertCanonicalAtwPath(path: unknown): asserts path is AtwPath {
  if (typeof path !== "string" || !path.startsWith("/") || normalizeDecoded(path) !== path) {
    throw new AtwPathError("path is not in canonical decoded form");
  }
}
